import "@/App.css";
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { CircleNotch, SignOut } from "@phosphor-icons/react";
import { Toaster } from "@/components/ui/sonner";
import Dashboard from "@/pages/Dashboard";
import NewChallenge from "@/pages/NewChallenge";
import SessionView from "@/pages/SessionView";
import PrintReport from "@/pages/PrintReport";
import Auth from "@/pages/Auth";
import ResetPassword from "@/pages/ResetPassword";
import Landing from "@/pages/Landing";
import AdminDashboard from "@/pages/AdminDashboard";
import DemoWalkthrough from "@/pages/DemoWalkthrough";
import ThemeToggle from "@/components/ThemeToggle";
import cgiLogo from "@/assets/cgi-logo.png";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { FeedbackProvider } from "@/context/FeedbackContext";
import { ShieldCheck } from "@phosphor-icons/react";

const Header = () => {
  const loc = useLocation();
  const { user, logout } = useAuth();
  const hidden = loc.pathname.endsWith("/report") || loc.pathname === "/login" || loc.pathname === "/reset-password" || loc.pathname === "/demo";
  // Anonymous visitors on the landing page get the Landing's own nav, not the app header.
  if (hidden || !user) return null;
  return (
    <header className="sticky top-0 z-40 border-b border-border bg-background/85 backdrop-blur-md">
      <div className="flex h-16 w-full items-center justify-between px-8">
        <div className="flex items-center gap-4">
          <a
            href="https://www.commongroundinstitute.net"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cgi-logo-link"
            title="Common Ground Institute"
            className="shrink-0 transition-opacity hover:opacity-80"
          >
            <img src={cgiLogo} width="1200" height="415" alt="Common Ground Institute" className="h-8 w-auto" />
          </a>
          <span className="hidden h-6 w-px bg-border sm:block" />
          <Link to="/" data-testid="app-home-link" className="group flex items-baseline gap-3">
            <span className="font-serif text-2xl leading-none tracking-tight">Executive Strategy Room</span>
          </Link>
        </div>
        <div className="flex items-center gap-2">
          {user.role === "admin" && loc.pathname !== "/admin" && (
            <Link to="/admin" data-testid="header-admin-link" className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-2 text-xs font-medium transition-colors hover:bg-muted"><ShieldCheck size={14} /> Admin</Link>
          )}
          {loc.pathname !== "/new" && (
            <Link to="/new" data-testid="header-new-challenge-btn" className="rounded-sm border border-primary bg-primary px-4 py-2 text-xs font-medium tracking-wide text-primary-foreground transition-colors hover:bg-primary/85">Start New Challenge</Link>
          )}
          <button data-testid="logout-btn" onClick={logout} title={`Sign out (${user.email})`} className="inline-flex items-center gap-1.5 rounded-sm border border-border px-3 py-2 text-xs font-medium transition-colors hover:bg-muted">
            <SignOut size={14} /> Sign out
          </button>
          <ThemeToggle />
        </div>
      </div>
    </header>
  );
};

const Loading = () => (
  <div className="grid min-h-screen place-items-center text-muted-foreground"><CircleNotch size={22} className="animate-spin" /></div>
);

// "/" is public: landing for anonymous visitors, dashboard for signed-in users.
const Home = () => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  return user ? <Dashboard /> : <Landing />;
};

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  const loc = useLocation();
  if (loc.pathname.startsWith("/sessions/demo/")) return children;
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  if (user.role !== "admin") return <Navigate to="/" replace />;
  return children;
};

function App() {
  return (
    <ThemeProvider attribute="class" defaultTheme="light" enableSystem={false}>
      <div className="App" style={{ paddingBottom: 40 }}>
        <BrowserRouter>
          <AuthProvider>
            <FeedbackProvider>
              <Header />
              <Routes>
                <Route path="/login" element={<Auth />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/" element={<Home />} />
                <Route path="/demo" element={<DemoWalkthrough />} />
                <Route path="/new" element={<ProtectedRoute><NewChallenge /></ProtectedRoute>} />
                <Route path="/sessions/:id" element={<ProtectedRoute><SessionView /></ProtectedRoute>} />
                <Route path="/sessions/:id/report" element={<ProtectedRoute><PrintReport /></ProtectedRoute>} />
                <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              </Routes>
            </FeedbackProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster position="top-right" />
      </div>
    </ThemeProvider>
  );
}

export default App;
